import React from 'react';

export interface EntityAction {
    id: string;
    label: string;
    icon: string;
    component: React.ComponentType<any>;
    requiresAuth?: boolean;
    permissions?: string[];
}

export interface EntityActionConfig {
    entityType: string;
    actions: EntityAction[];
}

class EntityActionRegistry {
    private static instance: EntityActionRegistry;
    private actionConfigs: Map<string, EntityActionConfig> = new Map();

    public static getInstance(): EntityActionRegistry {
        if (!EntityActionRegistry.instance) {
            EntityActionRegistry.instance = new EntityActionRegistry();
        }
        return EntityActionRegistry.instance;
    }

    /**
     * Register actions for a specific entity type
     */
    public registerActions(entityType: string, actions: EntityAction[]): void {
        const config: EntityActionConfig = {
            entityType,
            actions
        };
        this.actionConfigs.set(entityType.toLowerCase(), config);
    }

    /**
     * Get all actions for a specific entity type
     */
    public getActions(entityType: string): EntityAction[] {
        const config = this.actionConfigs.get(entityType.toLowerCase());
        return config ? config.actions : [];
    }

    /**
     * Get a specific action for an entity type
     */
    public getAction(entityType: string, actionId: string): EntityAction | null {
        const actions = this.getActions(entityType);
        return actions.find(action => action.id === actionId) || null;
    }

    /**
     * Check if an entity type has any registered actions
     */
    public hasActions(entityType: string): boolean {
        return this.getActions(entityType).length > 0;
    }

    /**
     * Get all registered entity types with actions
     */
    public getEntityTypesWithActions(): string[] {
        return Array.from(this.actionConfigs.keys());
    }

    /**
     * Clear all registered actions (useful for testing)
     */
    public clearAll(): void {
        this.actionConfigs.clear();
    }
}

export default EntityActionRegistry;
